import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ArrowLeft, ChevronRight, ChevronLeft, CalendarRange, Building2, Users, UserCog, Utensils, Home, Zap, Wallet, Receipt } from "lucide-react";
import api, { fmtEur, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { monthLabel } from "../components/MonthlyModule";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const ZERO = { ricavi: 0, retta_prevista: 0, spese: 0, vitto: 0, affitto: 0, utenze: 0, varie: 0, personale: 0, risultato: 0 };
const KEYS = ["ricavi", "retta_prevista", "spese", "vitto", "affitto", "utenze", "varie", "personale", "risultato"];
const pad = (n) => String(n).padStart(2, "0");

const Stat = ({ label, value, color, testid }) => (
  <div className="gr-card p-4" data-testid={testid}>
    <div className="text-xs text-muted-foreground font-semibold">{label}</div>
    <div className="text-xl font-head font-bold tabnum mt-1" style={{ color }}>{value}</div>
  </div>
);

// somma i record per mese (accorpa più strutture sullo stesso mese)
function monthMap(records) {
  const m = {};
  records.forEach((r) => {
    if (!m[r.mese]) m[r.mese] = { ...ZERO };
    KEYS.forEach((k) => { m[r.mese][k] += Number(r[k] || 0); });
  });
  return m;
}
function yearRows(map, yr) {
  return Array.from({ length: 12 }, (_, i) => {
    const key = `${yr}-${pad(i + 1)}`;
    return { mese: key, label: MESI[i], ...(map[key] || ZERO) };
  });
}
function sumRows(rows) {
  return rows.reduce((a, r) => {
    const o = { ...a }; KEYS.forEach((k) => { o[k] = a[k] + Number(r[k] || 0); }); return o;
  }, { ...ZERO });
}

// tabella annuale riutilizzabile: 12 mesi + riga totale cliccabile
function AnnualTable({ records, year, title, color, onMonth, onTotal, idPrefix }) {
  const map = monthMap(records);
  const rows = yearRows(map, year);
  const tot = sumRows(rows);
  return (
    <div className="gr-card overflow-hidden mb-6" data-testid={`ctrl-block-${idPrefix}`}>
      {title && (
        <div className="px-4 py-3 flex items-center gap-2 border-b bg-secondary/40">
          <Building2 size={16} style={{ color }} />
          <span className="font-head font-bold">{title}</span>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/60">
            <TableHead className="font-bold">Mese</TableHead>
            <TableHead className="font-bold text-right">Ricavi</TableHead>
            <TableHead className="font-bold text-right">Retta prevista</TableHead>
            <TableHead className="font-bold text-right">Varie</TableHead>
            <TableHead className="font-bold text-right">Vitto</TableHead>
            <TableHead className="font-bold text-right">Affitto</TableHead>
            <TableHead className="font-bold text-right">Utenze</TableHead>
            <TableHead className="font-bold text-right">Personale</TableHead>
            <TableHead className="font-bold text-right">Risultato</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.mese} className="cursor-pointer hover:bg-secondary/40" onClick={() => onMonth(r.mese)} data-testid={`${idPrefix}-month-${r.mese}`}>
              <TableCell className="font-semibold">{r.label} {year}</TableCell>
              <TableCell className="text-right tabnum">{fmtEur(r.ricavi)}</TableCell>
              <TableCell className="text-right tabnum text-muted-foreground">{fmtEur(r.retta_prevista)}</TableCell>
              <TableCell className="text-right tabnum text-red-600">{fmtEur(r.varie)}</TableCell>
              <TableCell className="text-right tabnum text-amber-700">{fmtEur(r.vitto)}</TableCell>
              <TableCell className="text-right tabnum text-orange-700">{fmtEur(r.affitto)}</TableCell>
              <TableCell className="text-right tabnum">{fmtEur(r.utenze)}</TableCell>
              <TableCell className="text-right tabnum">{fmtEur(r.personale)}</TableCell>
              <TableCell className="text-right tabnum font-bold" style={{ color: r.risultato >= 0 ? "#15803d" : "#dc2626" }}>{fmtEur(r.risultato)}</TableCell>
              <TableCell><ChevronRight size={16} className="text-muted-foreground" /></TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-secondary/40 font-bold cursor-pointer hover:bg-secondary/60" onClick={() => onTotal(tot)} data-testid={`${idPrefix}-total`}>
            <TableCell>Totale {year}</TableCell>
            <TableCell className="text-right tabnum">{fmtEur(tot.ricavi)}</TableCell>
            <TableCell className="text-right tabnum text-muted-foreground">{fmtEur(tot.retta_prevista)}</TableCell>
            <TableCell className="text-right tabnum text-red-600">{fmtEur(tot.varie)}</TableCell>
            <TableCell className="text-right tabnum text-amber-700">{fmtEur(tot.vitto)}</TableCell>
            <TableCell className="text-right tabnum text-orange-700">{fmtEur(tot.affitto)}</TableCell>
            <TableCell className="text-right tabnum">{fmtEur(tot.utenze)}</TableCell>
            <TableCell className="text-right tabnum">{fmtEur(tot.personale)}</TableCell>
            <TableCell className="text-right tabnum" style={{ color: tot.risultato >= 0 ? "#15803d" : "#dc2626" }}>{fmtEur(tot.risultato)}</TableCell>
            <TableCell><ChevronRight size={16} className="text-muted-foreground" /></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export default function Controllo() {
  const { selectedStructure, structures } = useAuth();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [detail, setDetail] = useState(null); // { r, title }
  const { data: mensile = [] } = useQuery({ queryKey: ["controllo-mensile", selectedStructure], queryFn: async () => (await api.get("/controllo/mensile", { params: { structure_id: selectedStructure } })).data });

  const yearOptions = Array.from(new Set([...mensile.map((r) => r.mese.slice(0, 4)), String(new Date().getFullYear()), year])).sort().reverse();
  const isAll = selectedStructure === "all";
  // strutture effettivamente presenti nei dati (in ordine alfabetico)
  const structIds = Array.from(new Set(mensile.map((r) => r.structure_id).filter(Boolean)));
  const structList = structuresSorted(structures, structIds);
  const multi = isAll && structList.length > 1;

  const openMonth = (records, mese, structId) => {
    const r = monthMap(records.filter((x) => x.mese === mese))[mese] || { ...ZERO };
    setDetail({ mode: "mese", r, mese, structId: structId || "all", title: monthLabel(mese) });
  };
  const openTotal = (r, title) => setDetail({ mode: "anno", r, title });

  if (detail) {
    if (detail.mode === "mese") return <MonthDetail detail={detail} onBack={() => setDetail(null)} />;
    const r = detail.r;
    const chart = [
      { name: "Ricavi incassati", v: r.ricavi || 0, c: "#1f7a5c" },
      { name: "Varie", v: r.varie || 0, c: "#dc2626" },
      { name: "Utenze", v: r.utenze || 0, c: "#c2660c" },
      { name: "Personale", v: r.personale || 0, c: "#2f6fb0" },
    ];
    return (
      <div>
        <Button variant="ghost" onClick={() => setDetail(null)} className="mb-3" data-testid="ctrl-back"><ArrowLeft size={16} className="mr-1" /> Indietro</Button>
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground"><CalendarRange size={16} /> Ricavi - Costi — <span className="font-bold text-foreground">{detail.title}</span></div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Stat label="Ricavi incassati" value={fmtEur(r.ricavi)} color="#1f7a5c" testid="ctrl-incassati" />
          <Stat label="Varie" value={fmtEur(r.varie)} color="#dc2626" testid="ctrl-spese" />
          <Stat label="Vitto" value={fmtEur(r.vitto)} color="#b45309" testid="ctrl-vitto" />
          <Stat label="Affitto" value={fmtEur(r.affitto)} color="#c2410c" testid="ctrl-affitto" />
          <Stat label="Utenze" value={fmtEur(r.utenze)} color="#c2660c" testid="ctrl-utenze" />
          <Stat label="Costo personale" value={fmtEur(r.personale)} color="#2f6fb0" testid="ctrl-personale" />
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="gr-card p-5">
            <h2 className="font-head font-bold mb-4">Composizione economica</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chart}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => fmtEur(v)} /><Bar dataKey="v" radius={[6, 6, 0, 0]}>{chart.map((e, i) => <Cell key={i} fill={e.c} />)}</Bar></BarChart>
            </ResponsiveContainer>
          </div>
          <div className="gr-card p-5 flex flex-col justify-center">
            <div className="p-5 rounded-xl" style={{ background: (r.risultato || 0) >= 0 ? "#dcfce7" : "#fee2e2" }} data-testid="ctrl-risultato">
              <div className="text-sm font-semibold text-muted-foreground">Risultato gestionale (cassa)</div>
              <div className="text-3xl font-head font-bold tabnum mt-1" style={{ color: (r.risultato || 0) >= 0 ? "#15803d" : "#dc2626" }}>{fmtEur(r.risultato)}</div>
              <div className="text-xs text-muted-foreground mt-3">Ricavi incassati − Spese totali (Vitto, Affitto, Utenze, Varie) − Costo del personale</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold">Ricavi - Costi</h1>
          <p className="text-sm text-muted-foreground">
            {multi ? "Dettaglio per struttura + totale complessivo · " : ""}seleziona un mese o il totale per il dettaglio
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground">Anno:</span>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28 font-semibold" data-testid="ctrl-year"><SelectValue /></SelectTrigger>
            <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {multi ? (
        <>
          <div className="mb-2 flex items-center gap-2 text-sm font-head font-bold text-primary">Totale complessivo (tutte le strutture)</div>
          <AnnualTable
            records={mensile}
            year={year}
            title="Totale complessivo"
            color="#2f6fb0"
            idPrefix="ctrl-grand"
            onMonth={(m) => openMonth(mensile, m, "all")}
            onTotal={(tot) => openTotal(tot, `Totale complessivo ${year}`)}
          />
          {structList.map((s) => {
            const records = mensile.filter((r) => r.structure_id === s.id);
            return (
              <AnnualTable
                key={s.id}
                records={records}
                year={year}
                title={s.nome}
                color={s.colore || "#1f7a5c"}
                idPrefix={`ctrl-s-${s.id}`}
                onMonth={(m) => openMonth(records, m, s.id)}
                onTotal={(tot) => openTotal(tot, `${s.nome} — Totale ${year}`)}
              />
            );
          })}
        </>
      ) : (
        <AnnualTable
          records={mensile}
          year={year}
          idPrefix="ctrl"
          onMonth={(m) => openMonth(mensile, m, selectedStructure)}
          onTotal={(tot) => openTotal(tot, `Totale ${year}`)}
        />
      )}
    </div>
  );
}

function MiniStat({ label, value, sub, color, testid }) {
  return (
    <div className="rounded-lg bg-secondary/40 px-3 py-2" data-testid={testid}>
      <div className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wide">{label}</div>
      <div className="text-lg font-head font-bold tabnum" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ListaSpese({ rows, cols, testid, empty, showStruct }) {
  if (!rows.length) return <div className="text-sm text-muted-foreground italic py-3" data-testid={`${testid}-empty`}>{empty}</div>;
  const allCols = showStruct
    ? [{ key: "structure_nome", label: "Struttura", className: "text-xs font-semibold text-primary" }, ...cols]
    : cols;
  return (
    <div className="max-h-72 overflow-y-auto -mx-1 px-1">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/50">
            {allCols.map((c) => <TableHead key={c.key} className={`font-bold ${c.right ? "text-right" : ""}`}>{c.label}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} data-testid={`${testid}-row-${i}`}>
              {allCols.map((c) => <TableCell key={c.key} className={`${c.right ? "text-right tabnum" : ""} ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key]}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SectionCard({ icon: Icon, title, total, color, children, testid }) {
  return (
    <div className="gr-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-head font-bold flex items-center gap-2"><Icon size={17} style={{ color }} /> {title}</div>
        {total != null && <div className="text-lg font-head font-bold tabnum" style={{ color }}>{fmtEur(total)}</div>}
      </div>
      {children}
    </div>
  );
}

function MonthDetail({ detail, onBack }) {
  const { selectedStructure } = useAuth();
  const structId = selectedStructure || "all";
  const showStruct = structId === "all";
  const [mese, setMese] = useState(detail.mese);
  const { data, isLoading } = useQuery({
    queryKey: ["controllo-dettaglio", mese, structId],
    queryFn: async () => (await api.get("/controllo/dettaglio", { params: { mese, structure_id: structId } })).data,
  });
  const r = mese === detail.mese ? detail.r : ZERO;
  const yr = mese.slice(0, 4);
  const shiftMonth = (delta) => {
    const [y, m] = mese.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMese(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  };

  return (
    <div className="pb-16">
      <Button variant="ghost" onClick={onBack} className="mb-3" data-testid="ctrl-back"><ArrowLeft size={16} className="mr-1" /> Indietro</Button>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground flex items-center gap-1.5"><CalendarRange size={16} /> Ricavi - Costi ·</span>
        <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(-1)} data-testid="ctrl-month-prev" title="Mese precedente"><ChevronLeft size={18} /></Button>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white shadow-sm" data-testid="ctrl-month-badge">
          <CalendarRange size={18} />
          <span className="font-head font-bold text-lg capitalize">{monthLabel(mese)}</span>
        </div>
        <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(1)} data-testid="ctrl-month-next" title="Mese successivo"><ChevronRight size={18} /></Button>
        <Select value={mese} onValueChange={setMese}>
          <SelectTrigger className="h-11 w-44 font-semibold" data-testid="ctrl-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MESI.map((nm, i) => <SelectItem key={i} value={`${yr}-${pad(i + 1)}`} data-testid={`ctrl-month-opt-${i + 1}`}>{nm} {yr}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Stat label="Ricavi incassati" value={fmtEur(data ? data.ricavi.incassato_tot : r.ricavi)} color="#1f7a5c" testid="ctrl-incassati" />
        <Stat label="Varie" value={fmtEur(data ? data.varie.totale : r.varie)} color="#dc2626" testid="ctrl-spese" />
        <Stat label="Vitto" value={fmtEur(data ? data.vitto.totale : r.vitto)} color="#b45309" testid="ctrl-vitto" />
        <Stat label="Affitto" value={fmtEur(data ? data.affitto.totale : r.affitto)} color="#c2410c" testid="ctrl-affitto" />
        <Stat label="Utenze" value={fmtEur(data ? data.utenze.totale : r.utenze)} color="#c2660c" testid="ctrl-utenze" />
        <Stat label="Costo personale" value={fmtEur(data ? data.personale.totale : r.personale)} color="#2f6fb0" testid="ctrl-personale" />
      </div>

      {isLoading || !data ? (
        <div className="gr-card p-10 text-center text-muted-foreground">Caricamento dettaglio...</div>
      ) : (
        <div className="space-y-6" data-testid="ctrl-dettaglio">
          {/* RICAVI + PERSONALE */}
          <div className="grid lg:grid-cols-2 gap-6">
            <SectionCard icon={Receipt} title="Ricavi — Rette ospiti" total={data.ricavi.incassato_tot} color="#1f7a5c" testid="dett-ricavi">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <MiniStat label="Ospiti" value={data.ricavi.n_ospiti} color="#1f7a5c" testid="dett-ricavi-nospiti" />
                <MiniStat label="Retta media" value={fmtEur(data.ricavi.retta_media)} sub="per ospite" color="#1f7a5c" testid="dett-ricavi-media" />
                <MiniStat label="Retta prevista" value={fmtEur(data.ricavi.retta_prevista_tot)} sub={`incassato ${fmtEur(data.ricavi.incassato_tot)}`} color="#64748b" testid="dett-ricavi-prevista" />
              </div>
              <ListaSpese showStruct={showStruct} testid="dett-ricavi-lista" empty="Nessuna retta registrata per questo mese."
                rows={data.ricavi.lista}
                cols={[
                  { key: "ospite_nome", label: "Ospite", className: "font-semibold" },
                  { key: "retta_prevista", label: "Retta prevista", right: true, render: (x) => fmtEur(x.retta_prevista) },
                  { key: "incassato", label: "Incassato", right: true, render: (x) => fmtEur(x.incassato) },
                ]} />
            </SectionCard>

            <SectionCard icon={UserCog} title="Costo del personale" total={data.personale.totale} color="#2f6fb0" testid="dett-personale">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <MiniStat label="Assunti" value={data.personale.n_operatori} color="#2f6fb0" testid="dett-pers-nop" />
                <MiniStat label="Media" value={fmtEur(data.personale.media)} sub="per assunto" color="#2f6fb0" testid="dett-pers-media" />
                <MiniStat label="Costo totale" value={fmtEur(data.personale.totale)} sub="costo aziendale" color="#2f6fb0" testid="dett-pers-tot" />
              </div>
              <ListaSpese showStruct={showStruct} testid="dett-personale-lista" empty="Nessun costo del personale caricato per questo mese (vedi Buste paga)."
                rows={data.personale.lista}
                cols={[
                  { key: "operatore_nome", label: "Operatore", className: "font-semibold" },
                  { key: "netto", label: "Netto", right: true, render: (x) => fmtEur(x.netto) },
                  { key: "totale_costo", label: "Costo aziendale", right: true, render: (x) => fmtEur(x.totale_costo) },
                ]} />
            </SectionCard>
          </div>

          {/* VITTO + UTENZE */}
          <div className="grid lg:grid-cols-2 gap-6">
            <SectionCard icon={Utensils} title="Vitto — spese alimentari" total={data.vitto.totale} color="#b45309" testid="dett-vitto">
              <ListaSpese showStruct={showStruct} testid="dett-vitto-lista" empty="Nessuna spesa di vitto per questo mese."
                rows={data.vitto.lista}
                cols={[
                  { key: "fornitore", label: "Fornitore", className: "font-semibold" },
                  { key: "descrizione", label: "Descrizione", render: (x) => x.descrizione || "—" },
                  { key: "data", label: "Data", render: (x) => x.data ? fmtDate(x.data) : "—" },
                  { key: "totale", label: "Totale", right: true, render: (x) => fmtEur(x.totale) },
                ]} />
            </SectionCard>

            <SectionCard icon={Zap} title="Utenze" total={data.utenze.totale} color="#c2660c" testid="dett-utenze">
              <ListaSpese showStruct={showStruct} testid="dett-utenze-lista" empty="Nessuna utenza per questo mese."
                rows={data.utenze.lista}
                cols={[
                  { key: "fornitore", label: "Fornitore", className: "font-semibold" },
                  { key: "descrizione", label: "Tipo / Descrizione", render: (x) => x.descrizione || "—" },
                  { key: "data", label: "Data", render: (x) => x.data ? fmtDate(x.data) : "—" },
                  { key: "totale", label: "Importo", right: true, render: (x) => fmtEur(x.totale) },
                ]} />
            </SectionCard>
          </div>

          {/* AFFITTO + SPESE GENERALI */}
          <div className="grid lg:grid-cols-2 gap-6">
            <SectionCard icon={Home} title="Affitto — canoni di locazione" total={data.affitto.totale} color="#c2410c" testid="dett-affitto">
              <ListaSpese showStruct={showStruct} testid="dett-affitto-lista" empty="Nessun canone di affitto per questo mese."
                rows={data.affitto.lista}
                cols={[
                  { key: "fornitore", label: "Locatore / Fornitore", className: "font-semibold" },
                  { key: "descrizione", label: "Descrizione", render: (x) => x.descrizione || "—" },
                  { key: "data", label: "Data", render: (x) => x.data ? fmtDate(x.data) : "—" },
                  { key: "totale", label: "Totale", right: true, render: (x) => fmtEur(x.totale) },
                ]} />
            </SectionCard>

            <SectionCard icon={Wallet} title={`Varie (${data.varie.n})`} total={data.varie.totale} color="#dc2626" testid="dett-spese">
              <ListaSpese showStruct={showStruct} testid="dett-spese-lista" empty="Nessuna spesa varia per questo mese."
                rows={data.varie.lista}
                cols={[
                  { key: "fornitore", label: "Fornitore", className: "font-semibold" },
                  { key: "categoria", label: "Categoria", className: "capitalize" },
                  { key: "data", label: "Data", render: (x) => x.data ? fmtDate(x.data) : "—" },
                  { key: "totale", label: "Totale", right: true, render: (x) => fmtEur(x.totale) },
                ]} />
            </SectionCard>
          </div>

          {/* RISULTATO */}
          <div className="p-5 rounded-xl" style={{ background: (data.risultato || 0) >= 0 ? "#dcfce7" : "#fee2e2" }} data-testid="ctrl-risultato">
            <div className="text-sm font-semibold text-muted-foreground">Risultato gestionale (cassa)</div>
            <div className="text-3xl font-head font-bold tabnum mt-1" style={{ color: (data.risultato || 0) >= 0 ? "#15803d" : "#dc2626" }}>{fmtEur(data.risultato)}</div>
            <div className="text-xs text-muted-foreground mt-2">Ricavi incassati {fmtEur(data.ricavi.incassato_tot)} − Spese totali {fmtEur(data.spese.totale)} (Vitto {fmtEur(data.vitto.totale)} + Affitto {fmtEur(data.affitto.totale)} + Utenze {fmtEur(data.utenze.totale)} + Varie {fmtEur(data.varie.totale)}) − Personale {fmtEur(data.personale.totale)}</div>
          </div>
        </div>
      )}
    </div>
  );
}


function structuresSorted(structures, ids) {
  const map = {};
  (structures || []).forEach((s) => { map[s.id] = s; });
  const nmeOf = (id) => id === "generali" ? "Spese generali società" : (map[id]?.nome || "Struttura");
  const ord = (name) => { const n = (name || "").toLowerCase(); if (n.includes("generali")) return 9; if (n.includes("villa")) return 0; if (n.includes("boschetto")) return 1; if (n.includes("dependance") || n.includes("dépendance")) return 2; return 3; };
  return ids
    .map((id) => ({ id, nome: nmeOf(id), colore: id === "generali" ? "#6366f1" : map[id]?.colore }))
    .sort((a, b) => ord(a.nome) - ord(b.nome) || (a.nome || "").localeCompare(b.nome || "", "it"));
}
